
// File: src/pages/Login.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
// Using a custom button below for custom hover effects
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Store, Eye, EyeOff, Mail, Lock, Sparkles } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from?.pathname || '/dashboard';

  // Set page title
  useEffect(() => {
    document.title = 'Vixalor - Login';
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setIsLoading(true);
  try {
    await login(email, password);
    navigate(from, { replace: true });
  } catch (err: any) {
    toast.error(err.message || 'Login failed');
    console.error('Login.tsx error:', err);
  } finally {
    setIsLoading(false);
  }
};

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#050912] text-white">
      {/* Layered gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#050912] via-[#0d1731] to-[#060b18]" />
      <div className="absolute -top-20 -right-32 h-96 w-96 bg-purple-500/30 blur-[140px] animate-pulse" />
      <div className="absolute -bottom-32 -left-10 h-[28rem] w-[28rem] bg-indigo-500/30 blur-[160px] animate-pulse delay-700" />

      <div className="relative z-10 w-full max-w-md px-6 py-10">
        {/* Brand badge */}
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="inline-flex items-center justify-center p-[2px] rounded-3xl bg-gradient-to-br from-white/40 to-white/10">
            <div className="flex items-center gap-3 rounded-[26px] bg-[#0b1224]/90 px-5 py-3 shadow-[0_10px_40px_rgba(15,23,42,0.65)]">
              <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-[#7c5dfa] to-[#5b8def] flex items-center justify-center shadow-[0_10px_30px_rgba(124,93,250,0.4)]">
                <Store className="h-7 w-7 text-white" />
              </div>
              <div className="text-left">
                <p className="text-white/70 text-xs uppercase tracking-widest font-semibold">Vixalor</p>
                <p className="text-lg font-semibold text-white">Point of Sale Suite</p>
              </div>
            </div>
          </div>
        </div>

        {/* Elevated card */}
        <div className="mt-6 rounded-[32px] border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_25px_60px_rgba(5,9,18,0.9)]">
          <Card className="rounded-[32px] border-none bg-transparent text-white">
            <CardHeader className="text-center space-y-2 pb-2">
              <CardTitle className="text-3xl font-semibold text-white">Welcome back</CardTitle>
              <CardDescription className="text-base text-white/70">
                Sign in to continue to your intelligent dashboard
              </CardDescription>
            </CardHeader>

            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-6 pt-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white/80 uppercase text-xs tracking-widest">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/50" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      disabled={isLoading}
                      required
                      className="pl-12 h-12 rounded-2xl border-white/10 bg-white/5 text-white placeholder:text-white/60 focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:border-transparent"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white/80 uppercase text-xs tracking-widest">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/50" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      disabled={isLoading}
                      required
                      className="pl-12 pr-14 h-12 rounded-2xl border-white/10 bg-white/5 text-white placeholder:text-white/60 focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white focus:outline-none"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
              </CardContent>

              <CardFooter className="pt-2 pb-8">
                <button
                  type="submit"
                  disabled={isLoading}
                  className={[
                    'w-full h-12 rounded-2xl font-semibold text-white tracking-wide',
                    'bg-gradient-to-r from-[#a855f7] via-[#7c5dfa] to-[#5b8def]',
                    'shadow-[0_12px_35px_rgba(124,93,250,0.55)]',
                    'transition-all duration-300 ease-out',
                    'hover:shadow-[0_18px_45px_rgba(92,161,255,0.45)] hover:-translate-y-0.5',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#050912] focus-visible:ring-white/70',
                    isLoading ? 'opacity-80 cursor-progress' : 'cursor-pointer'
                  ].join(' ')}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-3">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Signing In...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <span>Sign In</span>
                      <Sparkles className="h-4 w-4" />
                    </div>
                  )}
                </button>
              </CardFooter>
            </form>
          </Card>
        </div>

        <div className="text-center mt-6">
          <p className="text-xs text-white/55">© {new Date().getFullYear()} Vixalor. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
